// Cache lookup helpers for incremental parser segment reuse.
// Key-match is preferred; fingerprint-match is fallback for moved segments.
import type { LuisIncrementalParserCache, LuisParsedSegmentCache, LuisScannedSegment } from './types'

// Build lookup map from fingerprint to reusable previous segments.
export function buildPreviousFingerprintMap(
  previousCache: LuisIncrementalParserCache | undefined,
  objectSignature: string,
): Map<string, LuisParsedSegmentCache[]> {
  const byFingerprint = new Map<string, LuisParsedSegmentCache[]>()
  // Return empty map when object-variable signature changed.
  if (!previousCache || previousCache.objectSignature !== objectSignature) {
    // Return empty map to disable cross-signature reuse.
    return byFingerprint
  } else {
    // Continue building reusable fingerprint lookup.
  }

  // Group previous segments by fingerprint for movement-tolerant reuse.
  for (const segment of previousCache.segments) {
    const list = byFingerprint.get(segment.fingerprint) ?? []
    list.push(segment)
    byFingerprint.set(segment.fingerprint, list)
  }

  // Return reusable fingerprint lookup map.
  return byFingerprint
}

// Resolve cached segment by key-first, then fingerprint fallback.
export function resolveReusableSegment(
  scannedSegment: LuisScannedSegment,
  fingerprint: string,
  previousByKey: Map<string, LuisParsedSegmentCache>,
  previousByFingerprint: Map<string, LuisParsedSegmentCache[]>,
  usedFingerprintSegments: Set<LuisParsedSegmentCache>,
): LuisParsedSegmentCache | null {
  const byKey = previousByKey.get(scannedSegment.key)
  // Reuse key-matched segment when kind and fingerprint are unchanged.
  if (byKey && byKey.kind === scannedSegment.kind && byKey.fingerprint === fingerprint) {
    // Return key-based reusable segment.
    return byKey
  } else {
    // Continue fingerprint fallback lookup.
  }

  const list = previousByFingerprint.get(fingerprint)
  // Return null when no fingerprint-matched segment exists.
  if (!list || list.length === 0) {
    // Return null for fresh parse path.
    return null
  } else {
    // Continue searching for unused compatible segment.
  }

  // Reuse first unused compatible segment with matching kind.
  for (const candidate of list) {
    if (candidate.kind === scannedSegment.kind && !usedFingerprintSegments.has(candidate)) {
      usedFingerprintSegments.add(candidate)
      // Return movement-tolerant reusable candidate.
      return candidate
    } else {
      // Continue candidate scan.
    }
  }

  // Return null when compatible reusable segment is not found.
  return null
}
